/**
 * Production adapters for the comparison tools — existing viewer APIs only:
 *  - scans, structure sets, alignment: the session `Patient` (`images`,
 *    `getAffectedStructureSets`, `findRegistrationsBetween`, registration
 *    `isApproved`), `createStructureSetReadModel`, the scan date
 *    (`firstSlice.resolvedScanDateTime`);
 *  - the comparison set, in the order a clinician would do it:
 *      1. switch to the earlier structure set (`setEnvironmentStructureSet`,
 *         as "Change structure set") and wait for it to load;
 *      2. "Copy to…" a NEW structure set on the current scan with the
 *         approved registration (`copyWithRegistration`) or, sharing a
 *         frame of reference, `copySameFrameOfReference` — the earlier
 *         contour is resampled onto this scan; the Copy dialog's empty-copy
 *         check is repeated;
 *      3. rename the copy "<name>_prior" (`RoiService.rename`);
 *      4. copy the current structure into the same new set
 *         (`copyIntoExisting`, same scan geometry);
 *      5. switch to the new set and wait until both structures are loaded.
 *    If a step fails before 5, the viewer is switched back to the set the
 *    clinician was on. The original structure sets are never modified.
 *  - comparison numbers: `env.getRoiVolumeCm3`, `env.getRoiStatistics`, and
 *    `SDFOperations.applyBooleanToNewTexture` (AND / AND-NOT) measured and
 *    freed at once — no structure is created.
 * Browser glue covered by the manual demo; the logic is unit-tested
 * through fakes.
 */
import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { useSessionOrNull } from '~/contexts/SessionContext';
import type { Image3D } from '~/domain/patient/image3d';
import type { Patient } from '~/domain/patient/patient';
import type { StructureSet } from '~/domain/patient/structureset';
import { createStructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import type { CopyStructureSetResult } from '~/domain/services/structureSetService/structureSetCopyService';
import type { Environment } from '~/engine/data/Environment';
import {
    BooleanOperation,
    SDFOperations,
} from '~/engine/util/algorithms/sdf/SDFOperations';
import { useCopyStructureSet } from '~/hooks/structure-set/useCopyStructureSet';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { environmentState } from '~/state/atoms/engineAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { projectStructureFacts } from '../core/engineStructureFacts';
import type { BringPriorDeps } from './bringPriorContour';
import type { CompareDeps } from './compareStructures';
import {
    dicomDateToIso,
    isTargetType,
    type RegistrationState,
    type Timepoint,
    type TimepointDeps,
} from './timepoints';

/** How long to wait for the viewer to load a structure set (ms). */
const LOAD_TIMEOUT_MS = 15000;
const LOAD_POLL_MS = 100;

async function waitFor(ready: () => boolean, what: string): Promise<void> {
    const started = Date.now();
    while (!ready()) {
        if (Date.now() - started > LOAD_TIMEOUT_MS) {
            throw new Error(what);
        }
        await new Promise((r) => setTimeout(r, LOAD_POLL_MS));
    }
}

const textureReady = (env: Environment, roiNumber: number) => {
    const t = env.getRoi(roiNumber)?.texture;
    return !!t && !t.isEmpty;
};

function registrationState(
    patient: Patient,
    image: Image3D,
    current: Image3D | undefined
): RegistrationState {
    if (!current || image === current) {
        return 'current';
    }
    if (image.frameOfReferenceUid === current.frameOfReferenceUid) {
        return 'same-frame';
    }
    const regs = patient.findRegistrationsBetween(image, current);
    if (regs.some((r) => r.isApproved)) {
        return 'approved';
    }
    return regs.length > 0 ? 'unapproved' : 'none';
}

function readTimepoints(
    patient: Patient | undefined,
    env: Environment | null
): ReadonlyArray<Timepoint> | undefined {
    if (!patient) {
        return undefined;
    }
    const current = env?.image?.parent;
    const activeUid = env?.structureSet?.sopInstanceUID;
    return patient.images.map((image) => ({
        imageUid: image.dicomUid,
        date: dicomDateToIso(image.firstSlice.resolvedScanDateTime),
        modality: String(image.firstSlice.modality ?? ''),
        description: image.firstSlice.seriesDescription ?? '',
        isCurrent: image === current,
        registration: registrationState(patient, image, current),
        structureSets: patient.getAffectedStructureSets(image).map((ss) => {
            const rm = createStructureSetReadModel(ss);
            return {
                uid: rm.dicomUid,
                label: rm.structureSetLabel,
                isActive: rm.dicomUid === activeUid,
                approvalStatus: rm.approvalStatus,
                targets: rm.roiArray.flatMap((roi) => {
                    const type = roi.interpretedType;
                    return isTargetType(type)
                        ? [
                              {
                                  roiNumber: roi.roiNumber,
                                  name: roi.name,
                                  type,
                                  contourCount: roi.contourCount,
                              },
                          ]
                        : [];
                }),
            };
        }),
    }));
}

/** The one ROI a copy produced, or why it was skipped. */
function onlyCopied(result: CopyStructureSetResult, which: string): number {
    if (result.copiedRoiNumbers.length === 1) {
        return result.copiedRoiNumbers[0];
    }
    const why = result.skippedRois[0]?.reason;
    throw new Error(
        `the ${which} structure could not be copied${why ? `: ${why}` : ''}`
    );
}

export function useTimepointDeps(): () => TimepointDeps {
    const env = useRecoilValue(environmentState);
    const session = useSessionOrNull();
    return useCallback(
        () => ({ timepoints: () => readTimepoints(session?.patient, env) }),
        [env, session]
    );
}

export function useBringPriorContourDeps(): () => Omit<
    BringPriorDeps,
    'pendingMutationExists'
> {
    const env = useRecoilValue(environmentState);
    const session = useSessionOrNull();
    const { copyWithRegistration, copySameFrameOfReference, copyIntoExisting } =
        useCopyStructureSet();
    const { setEnvironmentStructureSet } = useViewportEnvironment();

    return useCallback(
        () => ({
            timepoints: () => readTimepoints(session?.patient, env),
            currentContextId: () => env?.structureSet?.sopInstanceUID,
            buildComparisonSet: async (args) => {
                const patient = session?.patient;
                if (!patient || !env || !session) {
                    throw new Error('no patient is loaded');
                }
                const pid = patient.dataSet.PatientID;
                const activeUid = env.structureSet?.sopInstanceUID;
                const priorSS = patient.getStructureSetWithSopInstanceUid(
                    args.priorSetUid
                );
                const currentSS: StructureSet | undefined = activeUid
                    ? patient.getStructureSetWithSopInstanceUid(activeUid)
                    : undefined;
                const currentImage = currentSS?.image;
                if (!priorSS?.image || !currentSS || !currentImage) {
                    throw new Error('the structure sets are no longer loaded');
                }
                const sameFrame =
                    priorSS.image.frameOfReferenceUid ===
                    currentImage.frameOfReferenceUid;
                const registration = sameFrame
                    ? undefined
                    : patient
                          .findRegistrationsBetween(priorSS.image, currentImage)
                          .find((r) => r.isApproved);
                if (!sameFrame && !registration) {
                    throw new Error(
                        'there is no approved registration between the scans'
                    );
                }

                let switchedToResult = false;
                try {
                    // 1. The copy reads the earlier contour from the viewer.
                    setEnvironmentStructureSet([priorSS], pid);
                    await waitFor(
                        () =>
                            env.structureSet?.sopInstanceUID ===
                                args.priorSetUid &&
                            textureReady(env, args.priorRoiNumber),
                        'the earlier structure set did not load'
                    );
                    // 2. "Copy to…" a new set on the current scan.
                    const selected = new Set([args.priorRoiNumber]);
                    const first = registration
                        ? await copyWithRegistration(
                              priorSS,
                              currentImage,
                              registration,
                              patient,
                              selected
                          )
                        : await copySameFrameOfReference(
                              priorSS,
                              currentImage,
                              patient,
                              selected
                          );
                    const target = first.structureSet;
                    const priorCopy = onlyCopied(first, 'earlier');
                    // The Copy dialog's own empty-copy check.
                    const copiedDicom =
                        target.dicomStructureSet.getRoi(priorCopy);
                    if (
                        !copiedDicom?.contours.some((c) => c.data.length >= 3)
                    ) {
                        throw new Error(
                            'the earlier contour is empty on this scan after ' +
                                'resampling; check the registration'
                        );
                    }
                    // 3. Rename the copy.
                    const copiedRoi = target.rois.find(
                        (r) => r.roiNumber === priorCopy
                    );
                    if (!copiedRoi) {
                        throw new Error('the copied structure was not found');
                    }
                    session.roiService.rename(
                        { id: copiedRoi.id },
                        args.priorCopyName
                    );
                    // 4. The current structure into the same set.
                    const second = await copyIntoExisting(
                        currentSS,
                        target,
                        new Set([args.currentRoiNumber])
                    );
                    const currentCopy = onlyCopied(second, 'current');
                    // 5. Show the comparison set.
                    setEnvironmentStructureSet([target], pid);
                    switchedToResult = true;
                    await waitFor(
                        () =>
                            env.structureSet?.sopInstanceUID ===
                                target.dicomUid &&
                            textureReady(env, priorCopy) &&
                            textureReady(env, currentCopy),
                        'the comparison structure set did not load'
                    );
                    return { setLabel: target.structureSetLabel };
                } finally {
                    if (!switchedToResult) {
                        setEnvironmentStructureSet([currentSS], pid);
                    }
                }
            },
        }),
        [
            env,
            session,
            copyWithRegistration,
            copySameFrameOfReference,
            copyIntoExisting,
            setEnvironmentStructureSet,
        ]
    );
}

export function useCompareStructuresDeps(): () => CompareDeps {
    const env = useRecoilValue(environmentState);
    const structureSet = useRecoilValue(currentStructureSetState);
    const session = useSessionOrNull();

    return useCallback((): CompareDeps => {
        const uid = env?.structureSet?.sopInstanceUID;
        const live = uid
            ? session?.patient.getStructureSetWithSopInstanceUid(uid)
            : undefined;
        const model = live ? createStructureSetReadModel(live) : structureSet;
        return {
            structures: projectStructureFacts(structureSet, env),
            roiInfo: (n) => {
                const r = model?.roiArray.find((x) => x.roiNumber === n);
                return r
                    ? {
                          type: r.interpretedType,
                          algorithm: r.roiGenerationAlgorithm,
                      }
                    : undefined;
            },
            volumeCm3: (n) => env?.getRoiVolumeCm3(n) ?? Number.NaN,
            stats: (n) => {
                const s = env?.getRoiStatistics(n);
                return s
                    ? {
                          surfaceAreaMm2: s.surfaceAreaMm2,
                          sphericity: s.sphericity,
                          centerOfMassMm: s.centerOfMassMm
                              ? Array.from(s.centerOfMassMm)
                              : null,
                          boundingBox: s.boundingBox
                              ? {
                                    min: Array.from(s.boundingBox.min),
                                    max: Array.from(s.boundingBox.max),
                                }
                              : null,
                      }
                    : undefined;
            },
            booleanVolumeCm3: (a, b, op) => {
                const ta = env?.getRoi(a)?.texture;
                const tb = env?.getRoi(b)?.texture;
                if (!ta || !tb) {
                    throw new Error('a structure is not loaded');
                }
                const result = SDFOperations.applyBooleanToNewTexture(
                    ta,
                    tb,
                    op === 'and'
                        ? BooleanOperation.AND
                        : BooleanOperation.AND_NOT
                );
                try {
                    return result.volumeCm3;
                } finally {
                    result.delete();
                }
            },
            sliceSpacingMm: () => env?.image?.grid.resZ,
        };
    }, [env, structureSet, session]);
}
